import { canonicalizeNetlist, computeTapeoutCommitment, implementsNand, netlistHash, parseNetlist, simulateAllCases, } from "@tapeout/netlist";
import { decodeEventLog, parseEventLogs, } from "viem";
import { protocolErrorAbi } from "./errors.js";
import { projectCoreAbi } from "./project.js";
import { CURRENT_CONTRACT_VERSION } from "./protocolRules.js";
import { ProjectState, nativeAmount } from "./types.js";
export const TAPEOUT_FEE = nativeAmount(2000000000000000n);
export const MAX_REVEAL_BLOCKS = 192000n;
const minerIdInput = [{ name: "minerId", type: "uint256" }];
export const tapeoutProjectCoreAbi = [
    ...projectCoreAbi,
    {
        type: "function",
        name: "computeTapeoutCommitment",
        stateMutability: "view",
        inputs: [
            { name: "designer", type: "address" },
            { name: "receiver", type: "address" },
            { name: "sortedNandIds", type: "uint256[5]" },
            { name: "netlistHash", type: "bytes32" },
            { name: "userNonce", type: "bytes32" },
            { name: "salt", type: "bytes32" },
        ],
        outputs: [{ name: "commitment", type: "bytes32" }],
    },
    {
        type: "function",
        name: "commitTapeout",
        stateMutability: "nonpayable",
        inputs: [{ name: "commitment", type: "bytes32" }],
        outputs: [],
    },
    {
        type: "function",
        name: "revealTapeout",
        stateMutability: "payable",
        inputs: [
            { name: "receiver", type: "address" },
            { name: "sortedNandIds", type: "uint256[5]" },
            { name: "netlist", type: "bytes" },
            { name: "userNonce", type: "bytes32" },
            { name: "salt", type: "bytes32" },
        ],
        outputs: [{ name: "minerId", type: "uint256" }],
    },
    {
        type: "function",
        name: "clearExpiredCommit",
        stateMutability: "nonpayable",
        inputs: [
            { name: "designer", type: "address" },
            { name: "commitment", type: "bytes32" },
        ],
        outputs: [],
    },
    {
        type: "function",
        name: "tapeoutCommit",
        stateMutability: "view",
        inputs: [
            { name: "designer", type: "address" },
            { name: "commitment", type: "bytes32" },
        ],
        outputs: [
            { name: "commitBlock", type: "uint64" },
            { name: "deadlineBlock", type: "uint64" },
        ],
    },
    {
        type: "function",
        name: "previewMiner",
        stateMutability: "view",
        inputs: minerIdInput,
        outputs: [{ name: "valid", type: "bool" }],
    },
    {
        type: "function",
        name: "verifyMiner",
        stateMutability: "nonpayable",
        inputs: minerIdInput,
        outputs: [{ name: "verified", type: "bool" }],
    },
    {
        type: "function",
        name: "minerSourceNandIds",
        stateMutability: "view",
        inputs: minerIdInput,
        outputs: [{ name: "sourceNandIds", type: "uint256[5]" }],
    },
    {
        type: "function",
        name: "minerNetlist",
        stateMutability: "view",
        inputs: minerIdInput,
        outputs: [{ name: "netlist", type: "bytes" }],
    },
    {
        type: "function",
        name: "minerNetlistHash",
        stateMutability: "view",
        inputs: minerIdInput,
        outputs: [{ name: "hash", type: "bytes32" }],
    },
    {
        type: "function",
        name: "minerVerificationStatus",
        stateMutability: "view",
        inputs: minerIdInput,
        outputs: [{ name: "status", type: "uint8" }],
    },
];
export const minerNftAbi = [
    ...protocolErrorAbi,
    {
        type: "function",
        name: "ownerOf",
        stateMutability: "view",
        inputs: [{ name: "tokenId", type: "uint256" }],
        outputs: [{ name: "owner", type: "address" }],
    },
    {
        type: "function",
        name: "ownershipEpoch",
        stateMutability: "view",
        inputs: [{ name: "tokenId", type: "uint256" }],
        outputs: [{ name: "epoch", type: "uint64" }],
    },
];
export const tapeoutEventAbi = [
    {
        type: "event",
        name: "TapeoutCommitted",
        inputs: [
            { name: "designer", type: "address", indexed: true },
            { name: "commitment", type: "bytes32", indexed: true },
            { name: "commitBlock", type: "uint64", indexed: false },
            { name: "deadlineBlock", type: "uint64", indexed: false },
        ],
    },
    {
        type: "event",
        name: "TapeoutCommitCleared",
        inputs: [
            { name: "designer", type: "address", indexed: true },
            { name: "commitment", type: "bytes32", indexed: true },
            { name: "used", type: "bool", indexed: false },
        ],
    },
    {
        type: "event",
        name: "NandConsumed",
        inputs: [
            { name: "nandId", type: "uint256", indexed: true },
            { name: "minerId", type: "uint256", indexed: true },
            { name: "designer", type: "address", indexed: true },
        ],
    },
    {
        type: "event",
        name: "TapeoutRevealed",
        inputs: [
            { name: "minerId", type: "uint256", indexed: true },
            { name: "receiver", type: "address", indexed: true },
            { name: "designer", type: "address", indexed: true },
            { name: "sortedNandIds", type: "uint256[5]", indexed: false },
            { name: "netlistHash", type: "bytes32", indexed: false },
            { name: "netlist", type: "bytes", indexed: false },
            { name: "tapeoutFeeWei", type: "uint256", indexed: false },
            { name: "feeRefundable", type: "bool", indexed: false },
        ],
    },
    {
        type: "event",
        name: "MinerCreated",
        inputs: [
            { name: "minerId", type: "uint256", indexed: true },
            { name: "owner", type: "address", indexed: true },
            { name: "netlistHash", type: "bytes32", indexed: true },
            { name: "sourceNandIds", type: "uint256[5]", indexed: false },
            { name: "createdAt", type: "uint64", indexed: false },
            { name: "tapeoutFeeRefundable", type: "bool", indexed: false },
        ],
    },
    {
        type: "event",
        name: "MinerVerified",
        inputs: [
            { name: "minerId", type: "uint256", indexed: true },
            { name: "owner", type: "address", indexed: true },
            { name: "verifiedAt", type: "uint64", indexed: false },
            { name: "settledUnverifiedReward", type: "uint256", indexed: false },
            { name: "verifiedTotalWeightAfter", type: "uint256", indexed: false },
            { name: "unverifiedTotalWeightAfter", type: "uint256", indexed: false },
        ],
    },
];
export function sortTapeoutNandIds(ids) {
    if (ids.length !== 5)
        throw new RangeError("Tapeout requires exactly five NAND IDs");
    const sorted = [...ids].sort((left, right) => (left < right ? -1 : left > right ? 1 : 0));
    if (sorted.some((id) => id <= 0n))
        throw new RangeError("NAND IDs must be positive");
    for (let index = 1; index < sorted.length; index += 1) {
        if (sorted[index] === sorted[index - 1])
            throw new RangeError("NAND IDs must be unique");
    }
    return sorted;
}
export function prepareTapeoutCommitment(input) {
    const sortedNandIds = sortTapeoutNandIds(input.nandIds);
    const canonicalNetlist = canonicalizeNetlist(input.netlist);
    parseNetlist(canonicalNetlist);
    const hash = netlistHash(canonicalNetlist);
    const outputs = simulateAllCases(canonicalNetlist);
    return {
        sortedNandIds,
        canonicalNetlist,
        netlistHash: hash,
        outputs,
        implementsNand: implementsNand(canonicalNetlist),
        commitment: computeTapeoutCommitment({
            chainId: input.chainId,
            projectCore: input.projectCore,
            designer: input.designer,
            receiver: input.receiver,
            sortedNandIds,
            netlistHash: hash,
            userNonce: input.userNonce,
            salt: input.salt,
        }),
    };
}
export function canTapeout(state, paused) {
    return !paused && state !== ProjectState.FAILED;
}
/** Operators may design, but the supplied protocol always mints to the owner of all five NAND. */
export function validateTapeoutNandReceiver(owners, receiver, version = CURRENT_CONTRACT_VERSION) {
    if (owners.length !== 5)
        throw new RangeError("Tapeout requires five NAND owners");
    if (version === CURRENT_CONTRACT_VERSION &&
        owners.some((owner) => owner.toLowerCase() !== receiver.toLowerCase())) {
        throw new Error("Miner receiver must own all five NAND");
    }
}
export function parseTapeoutReceipt(receipt) {
    return parseEventLogs({ abi: tapeoutEventAbi, logs: receipt.logs, strict: false });
}
export function tapeoutRevealedFromReceipt(receipt) {
    for (const log of receipt.logs) {
        try {
            const decoded = decodeEventLog({
                abi: tapeoutEventAbi,
                eventName: "TapeoutRevealed",
                data: log.data,
                topics: log.topics,
            });
            if (decoded.eventName !== "TapeoutRevealed")
                continue;
            return decoded.args;
        }
        catch {
            // Receipts also contain ERC-721 burns and mint logs.
        }
    }
    return undefined;
}
//# sourceMappingURL=tapeout.js.map